import { useDocument, useFields, useSigners, useVerifications } from '@modusign/react-signing';
import { useSigningState } from '@modusign/react-signing/dist/hooks/internal/useSigningState';
import { FieldNode, Locale, SignerToReference } from '@modusign/react-signing/types';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import { toDocumentModelForSetup } from '@app/features/apply-react-signing/libs/signing-mapper';
import limitNameForAuth from '@app/libs/limitNameForAuth';
import { validateEmail } from '@app/libs/validation';
import workspaceService from '@app/libs/workspaceService';
import { getColumnValue, getRequesterInputValues } from '@app/store/bulkRequest/selectors';
import { modalOpen } from '@app/store/modal/actions';
import { postNewDocuments, RequestMethod } from '@app/store/transport-layer/document/documents';
import { postDocumentUpload } from '@app/store/transport-layer/upload/document/upload';
import { RootState } from '@app/store/types';
import { getUserID } from '@app/store/user/user/selectors';

const formattedLocales: Record<string, Locale> = {
  한국어: 'ko',
  영어: 'en',
  중국어: 'zh-CN',
  일본어: 'ja',
  베트남어: 'vi',
};

export default function useSendTestBulkRequest(taskId: string) {
  const dispatch = useDispatch();

  const userId = useSelector(getUserID) ?? '';

  const {
    requesterInputValues,
    title,
    pdfPassword,
    password,
    passwordHint,
    authName,
    authPhone,
    participantsLocal,
    certificationBizNumber,
    certificationBizName,
    participantName,
    messageFromRequester,
  } = useGetTaskColumnValues(taskId);

  const { document } = useDocument();
  const { state } = useSigningState();
  const { addVerification } = useVerifications();
  const { signers, changeSigners } = useSigners();
  const { changeFields, fields } = useFields();

  const sendTestBulkRequest = useCallback(
    async ({ contact, signingDuration }: { contact: string; signingDuration: number }) => {
      try {
        // 템플릿 PDF 서명 요청 PDF 업로드
        const { fileId } = await postDocumentUpload({ url: document.pdfFile.url });

        signers.forEach((signer) => {
          const isBulkRequestParticipant =
            !signer.contact && !signer.name && signer.signingMethod == null;
          if (isBulkRequestParticipant) {
            if (password) {
              addVerification(signer.id, {
                type: 'PASSWORD',
                passValue: password,
                hint: passwordHint ?? null,
              });
            }

            if (authName && authPhone) {
              addVerification(signer.id, {
                type: 'MOBILE_IDENTIFICATION',
                hint: null,
                passValue: `${limitNameForAuth(authName)}|${authPhone}`,
                displayName: authName,
                displayPhoneNumber: authPhone,
              });
            }

            if (certificationBizName && certificationBizNumber) {
              addVerification(signer.id, {
                type: 'D_CERT',
                displayBizNumber: certificationBizNumber,
                displayName: certificationBizName,
                passValue: `${certificationBizName}|${certificationBizNumber}`,
                hint: null,
              });
            }
          }
        });

        // requesterField 간의 index가 대량전송 입력과 필드를 매핑할 때 필요함.
        const requesterFieldIndexMap: Record<string, number> = {};
        fields
          .filter((f) => f.participantReference.type === 'REQUESTER')
          .forEach((f, index) => {
            requesterFieldIndexMap[f.id] = index;
          });

        changeFields((fields) => {
          return fields.map<FieldNode>((f) => {
            const field = f as FieldNode;

            if (field.participantReference.type !== 'REQUESTER') {
              return field;
            }
            const value: string = requesterInputValues[requesterFieldIndexMap[f.id]];
            switch (field.type) {
              case 'TEXT':
                return {
                  ...field,
                  input: field.input && {
                    ...field.input,
                    value,
                  },
                };
              case 'CHECKBOX':
                return {
                  ...field,
                  input: field.input && {
                    ...field.input,
                    value: value.toUpperCase() === 'TRUE',
                  },
                };
              case 'SIGNATURE':
              case 'CHECKBOX_GROUP':
              case 'IMAGE':
              default:
                return field;
            }
          });
        });

        changeSigners((signersRef) => {
          return signersRef.map<SignerToReference>((signer) => {
            const isBulkRequestParticipant =
              !signer.contact && !signer.name && signer.signingMethod == null;

            return isBulkRequestParticipant
              ? {
                  ...signer,
                  contact,
                  locale: participantsLocal ? formattedLocales[participantsLocal] || 'ko' : 'ko',
                  name: participantName,
                  signingDuration,
                  signingMethod: validateEmail(contact).success ? 'EMAIL' : 'KAKAO',
                  dataLabel: signer.id,
                  message: messageFromRequester || undefined,
                }
              : {
                  ...signer,
                  message: signer.message || undefined,
                };
          });
        });

        const data = toDocumentModelForSetup({
          // (2022.11.03) 템플릿에는 요청자 첨부파일을 구현하지 않았기 때문에 대량전송 또한 요청자 첨부파일이 없습니다.
          attachments: state.attachments.filter((a) => a.participantReference.type !== 'REQUESTER'),
          document: state.document,
          fields: state.fields,
          participants: state.participants,
          ui: state.ui,
          verifications: state.verifications,
        });

        // 서명요청
        await postNewDocuments(workspaceService.getWorkspaceId(), {
          title: `[테스트전송] ${title}`,
          file: {
            id: fileId,
          },
          pdfPassword: pdfPassword || null,
          participants: data.participants,
          attachments: data.attachments,
          fields: data.fields,
          fieldGroups: data.fieldGroups,
          verifications: data.verifications,
          scheduledDate: null,
          requestMethod: {
            path: `/users/${userId}/test`,
          } as RequestMethod, // FIXME: RequestMethod내 주석과 호응하지않음
        });

        dispatch(
          modalOpen('CONFIRM', {
            title: '테스트 전송 성공',
            content: `${contact}로 전송을 성공했습니다.\n전송한 문서는 내문서 메뉴에서 확인할 수 있습니다.`,
          })
        );
      } catch (error) {
        dispatch(
          modalOpen('CONFIRM', {
            title: '테스트 전송 실패',
            content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
          })
        );
      }
    },
    [
      addVerification,
      authName,
      authPhone,
      certificationBizName,
      certificationBizNumber,
      changeFields,
      changeSigners,
      dispatch,
      document.pdfFile.url,
      fields,
      messageFromRequester,
      participantName,
      participantsLocal,
      password,
      passwordHint,
      pdfPassword,
      requesterInputValues,
      signers,
      state,
      title,
      userId,
    ]
  );

  return {
    sendTestBulkRequest,
  };
}

function useGetTaskColumnValues(taskId: string) {
  const requesterInputValues = useSelector((state: RootState) =>
    getRequesterInputValues(state, { id: taskId })
  );

  const title = useSelector((state: RootState) =>
    getColumnValue(state, { id: taskId, type: 'DOCUMENT_TITLE' })
  );

  const pdfPassword = useSelector((state: RootState) =>
    getColumnValue(state, { id: taskId, type: 'PDF_PASSWORD' })
  );

  const password = useSelector((state: RootState) =>
    getColumnValue(state, { id: taskId, type: 'VERIFICATION_PASSWORD' })
  );

  const passwordHint = useSelector((state: RootState) =>
    getColumnValue(state, { id: taskId, type: 'VERIFICATION_PASSWORD_HINT' })
  );

  const authName = useSelector((state: RootState) =>
    getColumnValue(state, { id: taskId, type: 'VERIFICATION_MOBILE_IDENTIFICATION_NAME' })
  );

  const authPhone = useSelector((state: RootState) =>
    getColumnValue(state, { id: taskId, type: 'VERIFICATION_MOBILE_IDENTIFICATION_PHONE' })
  );

  const participantsLocal = useSelector((state: RootState) =>
    getColumnValue(state, { id: taskId, type: 'PARTICIPANT_LOCALE' })
  );

  const certificationBizNumber = useSelector((state: RootState) =>
    getColumnValue(state, { id: taskId, type: 'VERIFICATION_D_CERT_BIZ_NUMBER' })
  );

  const certificationBizName = useSelector((state: RootState) =>
    getColumnValue(state, { id: taskId, type: 'VERIFICATION_D_CERT_NAME' })
  );

  const participantName = useSelector((state: RootState) =>
    getColumnValue(state, { id: taskId, type: 'PARTICIPANT_NAME' })
  );

  const messageFromRequester = useSelector((state: RootState) =>
    getColumnValue(state, { id: taskId, type: 'MESSAGE_FROM_REQUESTER' })
  );

  return {
    requesterInputValues,
    title,
    pdfPassword,
    password,
    passwordHint,
    authName,
    authPhone,
    participantsLocal,
    certificationBizNumber,
    certificationBizName,
    participantName,
    messageFromRequester,
  };
}
